import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import { ListPageLayout } from "@dashboard/components/Layouts";
import { CustomerFormsTabs } from "@dashboard/customerForms/CustomerFormsTabs";
import { ListProps } from "@dashboard/types";
import { Box } from "@saleor/macaw-ui-next";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { NewsletterSubscriber, StaffNotificationRecipient } from "../../types";
import { AddRecipientDialog } from "../AddRecipientDialog/AddRecipientDialog";
import { NewsletterSubscribersList } from "../NewsletterSubscribersList/NewsletterSubscribersList";
import { StaffRecipientsList } from "../StaffRecipientsList/StaffRecipientsList";
import { messages } from "./messages";

interface NewsletterListPageProps
  extends Pick<ListProps, "disabled" | "settings" | "onUpdateListSettings"> {
  subscribers: NewsletterSubscriber[] | undefined;
  recipients: StaffNotificationRecipient[] | undefined;
  canManageSettings: boolean;
  addRecipientState: ConfirmButtonTransitionState;
  deleteRecipientState: ConfirmButtonTransitionState;
  unsubscribeState: ConfirmButtonTransitionState;
  onAddRecipient: (data: { email: string; active: boolean }) => Promise<boolean>;
  onToggleRecipient: (recipient: StaffNotificationRecipient) => void;
  onDeleteRecipient: (recipient: StaffNotificationRecipient) => Promise<boolean>;
  onUnsubscribe: (subscriber: NewsletterSubscriber) => Promise<boolean>;
}

export const NewsletterListPage: FC<NewsletterListPageProps> = ({
  subscribers,
  recipients,
  canManageSettings,
  disabled,
  settings,
  onUpdateListSettings,
  addRecipientState,
  deleteRecipientState,
  unsubscribeState,
  onAddRecipient,
  onToggleRecipient,
  onDeleteRecipient,
  onUnsubscribe,
}) => {
  const intl = useIntl();
  const [addOpen, setAddOpen] = useState(false);
  const [recipientToDelete, setRecipientToDelete] = useState<StaffNotificationRecipient | null>(
    null,
  );
  const [toUnsubscribe, setToUnsubscribe] = useState<NewsletterSubscriber | null>(null);

  return (
    <ListPageLayout>
      <TopNav
        title={<FormattedMessage {...messages.title} />}
        withoutBorder
        isAlignToRight={false}
      />
      <Box display="flex" flexDirection="column" gap={6}>
        <CustomerFormsTabs active="newsletter" />
        <NewsletterSubscribersList
          subscribers={subscribers}
          disabled={disabled}
          settings={settings}
          onUpdateListSettings={onUpdateListSettings}
          onUnsubscribe={setToUnsubscribe}
        />
        {canManageSettings && (
          <StaffRecipientsList
            recipients={recipients}
            disabled={disabled}
            onAdd={() => setAddOpen(true)}
            onToggle={onToggleRecipient}
            onDelete={setRecipientToDelete}
          />
        )}
      </Box>

      <AddRecipientDialog
        open={addOpen}
        confirmButtonState={addRecipientState}
        onClose={() => setAddOpen(false)}
        onSubmit={async data => {
          const ok = await onAddRecipient(data);

          if (ok) {
            setAddOpen(false);
          }
        }}
      />

      <ActionDialog
        open={!!recipientToDelete}
        variant="delete"
        title={intl.formatMessage(messages.deleteRecipientTitle)}
        confirmButtonState={deleteRecipientState}
        onClose={() => setRecipientToDelete(null)}
        onConfirm={async () => {
          if (recipientToDelete) {
            const ok = await onDeleteRecipient(recipientToDelete);

            if (ok) {
              setRecipientToDelete(null);
            }
          }
        }}
      >
        <FormattedMessage
          {...messages.deleteRecipientText}
          values={{
            email: recipientToDelete?.email ?? recipientToDelete?.user?.email ?? "",
          }}
        />
      </ActionDialog>

      <ActionDialog
        open={!!toUnsubscribe}
        variant="delete"
        title={intl.formatMessage(messages.unsubscribeTitle)}
        confirmButtonLabel={intl.formatMessage(messages.unsubscribe)}
        confirmButtonState={unsubscribeState}
        onClose={() => setToUnsubscribe(null)}
        onConfirm={async () => {
          if (toUnsubscribe) {
            const ok = await onUnsubscribe(toUnsubscribe);

            if (ok) {
              setToUnsubscribe(null);
            }
          }
        }}
      >
        <FormattedMessage
          {...messages.unsubscribeText}
          values={{
            name: toUnsubscribe?.email ?? "",
          }}
        />
      </ActionDialog>
    </ListPageLayout>
  );
};
